/**
 * Bayesian read of an A/B goal: the chance a version beats Control, and the
 * likely range of the relative change.
 *
 * Proportion goals (visitors who converted out of visitors):
 *   p ~ Beta(1 + conversions, 1 + visitors − conversions)   (uniform prior)
 *   chance = P(p_B > p_A), exact closed form (Miller 2015):
 *     Σ_{i=0}^{α_B−1} B(α_A + i, β_A + β_B) / ((β_B + i) · B(1 + i, β_B) · B(α_A, β_A))
 *   summed in log space over the smaller of α_A, α_B (by symmetry), and a
 *   normal approximation on p_B − p_A once both α exceed NORMAL_ABOVE.
 *
 * Count goals (events per visitor, e.g. every click):
 *   λ ~ Gamma(shape (1 + events) / φ, rate visitors / φ)
 *   (Gamma-Poisson with a flat-ish prior; φ ≥ 1 is the quasi-Poisson
 *   dispersion from the daily series. Dividing shape and rate by φ keeps the
 *   mean (1 + events) / visitors and multiplies the variance by φ.)
 *   chance = P(λ_B > λ_A) = I_x(shape_A, shape_B), x = rate_A / (rate_A + rate_B),
 *   because rate_A·λ_A / (rate_A·λ_A + rate_B·λ_B) ~ Beta(shape_A, shape_B).
 *
 * Likely range: the 2.5% and 97.5% quantiles of θ_B / θ_A − 1 from 20,000
 * draws of a fixed-seed generator (mulberry32 → Box–Muller → Marsaglia–Tsang
 * gamma; Beta = G_a / (G_a + G_b)), so the same inputs always give the same
 * range. Results are memoized by input.
 *
 * Revenue per visitor stays descriptive: no chance, no range.
 */

export interface BayesComparison {
  /** P(version beats Control), 0 to 1. */
  chance: number
  /** 95% credible interval of the relative change (θ_B / θ_A − 1), in percent; null without data. */
  range: { lower: number; upper: number; median: number } | null
}

export const RANGE_DRAWS = 20_000
const NORMAL_ABOVE = 50_000
const SEED = 0x9e3779b9

// ── Special functions ───────────────────────────────────────────────────

const LANCZOS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313, -176.61502916214059,
  12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
]

/** ln Γ(x) for x > 0 (Lanczos, g = 7; ~15 significant digits). */
export function lnGamma(x: number): number {
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - lnGamma(1 - x)
  const z = x - 1
  let a = LANCZOS[0]
  const t = z + 7.5
  for (let i = 1; i < 9; i++) a += LANCZOS[i] / (z + i)
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(a)
}

export const lnBeta = (a: number, b: number) => lnGamma(a) + lnGamma(b) - lnGamma(a + b)

/** Regularized incomplete beta I_x(a, b) (continued fraction, Lentz). */
export function incompleteBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0
  if (x >= 1) return 1
  const front = Math.exp(a * Math.log(x) + b * Math.log(1 - x) - lnBeta(a, b))
  // The continued fraction converges fast for x < (a + 1) / (a + b + 2).
  if (x > (a + 1) / (a + b + 2)) return 1 - incompleteBeta(1 - x, b, a)
  const TINY = 1e-300
  let f = 1
  let c = 1
  let d = 1 - ((a + b) * x) / (a + 1)
  if (Math.abs(d) < TINY) d = TINY
  d = 1 / d
  f = d
  for (let m = 1; m <= 20_000; m++) {
    const m2 = 2 * m
    let num = (m * (b - m) * x) / ((a + m2 - 1) * (a + m2))
    d = 1 + num * d
    if (Math.abs(d) < TINY) d = TINY
    c = 1 + num / c
    if (Math.abs(c) < TINY) c = TINY
    d = 1 / d
    f *= d * c
    num = (-(a + m) * (a + b + m) * x) / ((a + m2) * (a + m2 + 1))
    d = 1 + num * d
    if (Math.abs(d) < TINY) d = TINY
    c = 1 + num / c
    if (Math.abs(c) < TINY) c = TINY
    d = 1 / d
    const delta = d * c
    f *= delta
    if (Math.abs(delta - 1) < 1e-14) break
  }
  return (front * f) / a
}

/** Standard normal CDF (Abramowitz–Stegun 7.1.26 via erf, |error| < 1.5e-7). */
function normalCdf(z: number): number {
  const t = 1 / (1 + 0.3275911 * (Math.abs(z) / Math.SQRT2))
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-(z * z) / 2)
  return z >= 0 ? (1 + y) / 2 : (1 - y) / 2
}

// ── Chance to beat ──────────────────────────────────────────────────────

/** P(X_B > X_A) for X ~ Beta(a, b), exact for integer α_B (or α_A, by symmetry). */
export function betaChanceBeats(aA: number, bA: number, aB: number, bB: number): number {
  if (Math.min(aA, aB) > NORMAL_ABOVE) {
    const mean = (a: number, b: number) => a / (a + b)
    const variance = (a: number, b: number) => (a * b) / ((a + b) ** 2 * (a + b + 1))
    return normalCdf((mean(aB, bB) - mean(aA, bA)) / Math.sqrt(variance(aA, bA) + variance(aB, bB)))
  }
  // Sum over the smaller shape; P(B > A) = 1 − P(A > B) (ties have measure 0).
  if (aB > aA) return 1 - betaChanceBeats(aB, bB, aA, bA)
  const base = lnBeta(aA, bA)
  let total = 0
  for (let i = 0; i < aB; i++) {
    total += Math.exp(lnBeta(aA + i, bA + bB) - Math.log(bB + i) - lnBeta(1 + i, bB) - base)
  }
  return Math.min(1, Math.max(0, total))
}

/** P(λ_B > λ_A) for λ ~ Gamma(shape, rate). */
export function gammaChanceBeats(shapeA: number, rateA: number, shapeB: number, rateB: number): number {
  if (Math.min(shapeA, shapeB) > NORMAL_ABOVE) {
    const z = (shapeB / rateB - shapeA / rateA) / Math.sqrt(shapeA / rateA ** 2 + shapeB / rateB ** 2)
    return normalCdf(z)
  }
  return incompleteBeta(rateA / (rateA + rateB), shapeA, shapeB)
}

// ── Seeded sampling for the range ───────────────────────────────────────

function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function makeSampler(seed: number) {
  const uniform = mulberry32(seed)
  let spare: number | null = null
  const normal = () => {
    if (spare != null) {
      const s = spare
      spare = null
      return s
    }
    let u = 0
    while (u === 0) u = uniform()
    const v = uniform()
    const r = Math.sqrt(-2 * Math.log(u))
    spare = r * Math.sin(2 * Math.PI * v)
    return r * Math.cos(2 * Math.PI * v)
  }
  // Marsaglia–Tsang; shapes below 1 are boosted: G(a) = G(a + 1) · U^(1/a).
  const gamma = (shape: number): number => {
    if (shape < 1) {
      let u = 0
      while (u === 0) u = uniform()
      return gamma(shape + 1) * Math.pow(u, 1 / shape)
    }
    const d = shape - 1 / 3
    const c = 1 / Math.sqrt(9 * d)
    for (;;) {
      let x: number
      let v: number
      do {
        x = normal()
        v = 1 + c * x
      } while (v <= 0)
      v = v * v * v
      const u = uniform()
      if (u < 1 - 0.0331 * x ** 4 || Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v
    }
  }
  return { gamma }
}

function quantiles(draws: Float64Array) {
  draws.sort()
  const at = (q: number) => draws[Math.min(draws.length - 1, Math.max(0, Math.round(q * (draws.length - 1))))]
  return { lower: at(0.025) * 100, upper: at(0.975) * 100, median: at(0.5) * 100 }
}

function relativeRange(drawA: () => number, drawB: () => number) {
  const out = new Float64Array(RANGE_DRAWS)
  for (let i = 0; i < RANGE_DRAWS; i++) out[i] = drawB() / drawA() - 1
  return quantiles(out)
}

// ── Public comparisons (memoized) ───────────────────────────────────────

const cache = new Map<string, BayesComparison>()
const memo = (key: string, compute: () => BayesComparison) => {
  const hit = cache.get(key)
  if (hit) return hit
  const value = compute()
  if (cache.size > 500) cache.delete(cache.keys().next().value!)
  cache.set(key, value)
  return value
}

const NO_DATA: BayesComparison = { chance: 0.5, range: null }

/** Visitors who converted: Beta-Binomial with a uniform prior. */
export function compareProportions(conversionsA: number, visitorsA: number, conversionsB: number, visitorsB: number): BayesComparison {
  if (!(visitorsA > 0) || !(visitorsB > 0)) return NO_DATA
  const cA = Math.min(Math.max(0, Math.round(conversionsA)), Math.round(visitorsA))
  const cB = Math.min(Math.max(0, Math.round(conversionsB)), Math.round(visitorsB))
  const nA = Math.round(visitorsA)
  const nB = Math.round(visitorsB)
  return memo(`p:${cA}/${nA}:${cB}/${nB}`, () => {
    const aA = 1 + cA
    const bA = 1 + nA - cA
    const aB = 1 + cB
    const bB = 1 + nB - cB
    const { gamma } = makeSampler(SEED)
    const beta = (a: number, b: number) => () => {
      const x = gamma(a)
      return x / (x + gamma(b))
    }
    return { chance: betaChanceBeats(aA, bA, aB, bB), range: relativeRange(beta(aA, bA), beta(aB, bB)) }
  })
}

/** Events per visitor: Gamma-Poisson, variance inflated by `dispersion` (≥ 1). */
export function compareRates(eventsA: number, visitorsA: number, eventsB: number, visitorsB: number, dispersion = 1): BayesComparison {
  if (!(visitorsA > 0) || !(visitorsB > 0)) return NO_DATA
  const phi = Math.max(1, dispersion)
  return memo(`r:${eventsA}/${visitorsA}:${eventsB}/${visitorsB}:${phi.toFixed(4)}`, () => {
    const shapeA = (1 + Math.max(0, eventsA)) / phi
    const rateA = visitorsA / phi
    const shapeB = (1 + Math.max(0, eventsB)) / phi
    const rateB = visitorsB / phi
    const { gamma } = makeSampler(SEED)
    return {
      chance: gammaChanceBeats(shapeA, rateA, shapeB, rateB),
      range: relativeRange(() => gamma(shapeA) / rateA, () => gamma(shapeB) / rateB),
    }
  })
}

/** "93%", with the ends said honestly: ">99%" and "<1%". */
export function formatChance(chance: number): string {
  const pct = chance * 100
  if (pct > 99) return ">99%"
  if (pct < 1) return "<1%"
  return `${Math.round(pct)}%`
}

/** "+22% to +51%". */
export function formatRange(range: { lower: number; upper: number }): string {
  const f = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))}%`
  return `${f(range.lower)} to ${f(range.upper)}`
}

/**
 * P(each version is the best) for 3+ versions: the share of 20,000 joint
 * posterior draws (same priors and seeded generator as above) in which that
 * version has the highest rate. Index-aligned with `arms`. With two versions
 * this matches the closed-form chance up to sampling error, so callers keep
 * the closed form there.
 */
export function chanceBest(
  arms: Array<{ events: number; exposure: number }>,
  metric: "proportion" | "count",
  dispersion = 1,
): number[] {
  if (arms.length === 0 || arms.some((a) => !(a.exposure > 0))) return arms.map(() => 1 / Math.max(1, arms.length))
  const phi = Math.max(1, dispersion)
  const key = `best:${metric}:${phi.toFixed(4)}:${arms.map((a) => `${Math.round(a.events)}/${Math.round(a.exposure)}`).join(",")}`
  const hit = bestCache.get(key)
  if (hit) return hit
  const { gamma } = makeSampler(SEED)
  const draw = arms.map((a) => {
    if (metric === "count") {
      const shape = (1 + Math.max(0, a.events)) / phi
      const rate = a.exposure / phi
      return () => gamma(shape) / rate
    }
    const c = Math.min(Math.max(0, Math.round(a.events)), Math.round(a.exposure))
    const alpha = 1 + c
    const beta = 1 + Math.round(a.exposure) - c
    return () => {
      const x = gamma(alpha)
      return x / (x + gamma(beta))
    }
  })
  const wins = new Array(arms.length).fill(0)
  for (let i = 0; i < RANGE_DRAWS; i++) {
    let best = 0
    let bestValue = -Infinity
    for (let j = 0; j < draw.length; j++) {
      const v = draw[j]()
      if (v > bestValue) {
        bestValue = v
        best = j
      }
    }
    wins[best]++
  }
  const out = wins.map((w) => w / RANGE_DRAWS)
  if (bestCache.size > 200) bestCache.delete(bestCache.keys().next().value!)
  bestCache.set(key, out)
  return out
}

const bestCache = new Map<string, number[]>()
